import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Globe, ChevronDown, Calendar, Facebook, Instagram, Linkedin, Youtube, ExternalLink, FileText } from 'lucide-react';
import { getCurrentLanguage, setCurrentLanguage, getLanguages, getText } from '../services/dataSync';
import { fetchAllEventsPages } from '../services/api';
import { getEventStartEnd } from '../utils/eventDateTime';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isImportantLinksOpen, setIsImportantLinksOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [mobileImportantLinksOpen, setMobileImportantLinksOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [languages, setLanguages] = useState(() => {
    const langs = getLanguages();
    // Ensure languages object is not empty
    return Object.keys(langs).length > 0 ? langs : { en: { name: 'English', isDefault: true, content: {} }, mr: { name: 'मराठी', content: {} }, hi: { name: 'हिंदी', content: {} } };
  });
  const [navigation, setNavigation] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  // All refs declared
  const dropdownRef = useRef(null);
  const langDropdownRef = useRef(null);
  const aboutButtonRef = useRef(null);
  const importantLinksButtonRef = useRef(null);
  const importantLinksDropdownRef = useRef(null);

  // Social Media Links (without Twitter and WhatsApp)
  const socialMediaLinks = [
    {
      name: getText('nav.socialMedia.linkedin') || 'LinkedIn',
      icon: Linkedin,
      url: 'https://www.linkedin.com/in/industrial-fedration-476350396/',
    },
    {
      name: getText('nav.socialMedia.instagram') || 'Instagram',
      icon: Instagram,
      url: 'https://www.instagram.com/industrial_fedration/',
    },
    {
      name: getText('nav.socialMedia.facebook') || 'Facebook',
      icon: Facebook,
      url: 'https://www.facebook.com/profile.php?id=61583550305403&sk=about',
    },
    {
      name: getText('nav.socialMedia.youtube') || 'YouTube',
      icon: Youtube,
      url: 'https://www.youtube.com/channel/UCSih3s-oIay7uLFDKuLRatQ',
    }
  ];

  // Important Business Registration Links
  const importantLinks = [
    { name: getText('nav.importantLinks.rofPortal') || 'ROF | Portal', url: 'https://share.google/d49T4ntghgfOZIyEd' },
    { name: getText('nav.importantLinks.incomeTax') || 'Income Tax Department', url: 'https://share.google/auy1NFiXbZtYa30m4' },
    { name: getText('nav.importantLinks.mahagst') || 'MAHAGST', url: 'https://share.google/lP5CtxW5nHzsD1ycS' },
    { name: getText('nav.importantLinks.aapleSarkar') || 'Aaple Sarkar', url: 'https://share.google/7DhIL3GzAnz1vEmdI' },
    { name: getText('nav.importantLinks.esic') || 'ESIC', url: 'https://share.google/a3LmpqgA0V8RPnPyj' },
    { name: getText('nav.importantLinks.epfo') || 'EPFO', url: 'https://share.google/kU8INzjLfoJ27fIWj' }
  ];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check About dropdown
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        aboutButtonRef.current &&
        !aboutButtonRef.current.contains(event.target)
      ) {
        setIsAboutOpen(false);
      }
      // Check Important Links dropdown
      if (
        importantLinksDropdownRef.current && 
        !importantLinksDropdownRef.current.contains(event.target) &&
        importantLinksButtonRef.current &&
        !importantLinksButtonRef.current.contains(event.target)
      ) {
        setIsImportantLinksOpen(false);
      }
      // Check Language dropdown - use click event and check if target is not inside
      if (
        isLangOpen &&
        langDropdownRef.current && 
        !langDropdownRef.current.contains(event.target)
      ) {
        // Small delay to allow button click to process first
        setTimeout(() => {
          setIsLangOpen(false);
        }, 0);
      }
    };

    // Use click instead of mousedown to avoid interference
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isLangOpen]);

  // Update language state when it changes
  useEffect(() => {
    const updateLanguage = () => {
      const newLang = getCurrentLanguage();
      setCurrentLang(newLang);
      const langs = getLanguages();
      // Ensure languages object is not empty - always maintain valid languages
      const validLangs = Object.keys(langs).length > 0 ? langs : { en: { name: 'English', isDefault: true, content: {} }, mr: { name: 'मराठी', content: {} }, hi: { name: 'हिंदी', content: {} } };
      // Always update languages to ensure button stays visible
      setLanguages(validLangs);
      
      setNavigation([
        { name: getText('nav.home'), href: '/', current: location.pathname === '/' },
        { 
          name: getText('nav.about'), 
          href: '/about', 
          current: location.pathname.startsWith('/about'),
          hasDropdown: true,
          dropdownItems: [
            { name: getText('nav.about.submenu.about'), href: '/about' },
            { name: getText('nav.about.submenu.president'), href: '/about/president' },
            // { name: getText('nav.about.submenu.founder'), href: '/about/founder' },
            { name: getText('nav.about.submenu.mission'), href: '/about/mission-vision' },
            { name: getText('nav.about.submenu.values'), href: '/about/core-values' },
            { name: getText('nav.about.submenu.structure'), href: '/about/structure' },
            { name: getText('nav.about.submenu.leadershipTeam'), href: '/about/leadership-team' },
            // { name: getText('nav.about.submenu.achievements'), href: '/about/achievements' },
            { name: getText('nav.about.submenu.committee'), href: '/about/committee' }
          ]
        },
        { name: getText('nav.support'), href: '/services', current: location.pathname === '/services' },
        { name: getText('nav.events'), href: '/events', current: location.pathname === '/events' },
        { name: getText('nav.news') || 'News', href: '/news', current: location.pathname === '/news' },
        { name: getText('nav.membership'), href: '/membership', current: location.pathname === '/membership' },
        { name: getText('nav.gallery'), href: '/gallery', current: location.pathname === '/gallery' },
        { name: getText('nav.contact'), href: '/contact', current: location.pathname === '/contact' },
      ]);
    };

    const handleLanguageChange = () => {
      updateLanguage();
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', updateLanguage);
    
    updateLanguage();

    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', updateLanguage);
    };
  }, [location.pathname]);

  // Check if admin is logged in
  useEffect(() => {
    const checkAdminLogin = () => {
      const token = localStorage.getItem('ifm_admin_token');
      const isLoggedIn = localStorage.getItem('ifm_admin_logged_in');
      setIsAdminLoggedIn(!!(token && isLoggedIn === 'true'));
    };
    
    checkAdminLogin();
    
    const handleStorageChange = () => checkAdminLogin();
    window.addEventListener('storage', handleStorageChange);
    
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleLanguageChange = (languageCode) => {
    setCurrentLanguage(languageCode);
    setCurrentLang(languageCode);
    setIsLangOpen(false);
    
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: languageCode } }));
    
    setTimeout(() => {
      setNavigation([
        { name: getText('nav.home'), href: '/', current: location.pathname === '/' },
        { 
          name: getText('nav.about'), 
          href: '/about', 
          current: location.pathname.startsWith('/about'),
          hasDropdown: true,
          dropdownItems: [
            { name: getText('nav.about.submenu.about'), href: '/about' },
            { name: getText('nav.about.submenu.president'), href: '/about/president' },
            // { name: getText('nav.about.submenu.founder'), href: '/about/founder' },
            { name: getText('nav.about.submenu.mission'), href: '/about/mission-vision' },
            { name: getText('nav.about.submenu.values'), href: '/about/core-values' },
            { name: getText('nav.about.submenu.structure'), href: '/about/structure' },
            { name: getText('nav.about.submenu.leadershipTeam'), href: '/about/leadership-team' },
            // { name: getText('nav.about.submenu.achievements'), href: '/about/achievements' },
            { name: getText('nav.about.submenu.committee'), href: '/about/committee' }
          ]
        },
        { name: getText('nav.support'), href: '/services', current: location.pathname === '/services' },
        { name: getText('nav.events'), href: '/events', current: location.pathname === '/events' },
        { name: getText('nav.news') || 'News', href: '/news', current: location.pathname === '/news' },
        { name: getText('nav.partners') || 'Partners', href: '/partners', current: location.pathname === '/partners' },
        { name: getText('nav.membership'), href: '/membership', current: location.pathname === '/membership' },
        { name: getText('nav.gallery'), href: '/gallery', current: location.pathname === '/gallery' },
        { name: getText('nav.contact'), href: '/contact', current: location.pathname === '/contact' },
      ]);
    }, 100);
  };


  const getCurrentLanguageName = () => {
    // Always ensure we have a valid language name - never return empty or undefined
    try {
      if (languages && typeof languages === 'object' && Object.keys(languages).length > 0) {
        const langName = languages[currentLang]?.name || languages['en']?.name || 'English';
        return langName || 'English'; // Double check - never return empty string
      }
    } catch (e) {
      // If any error occurs, return default
    }
    // Always return a valid fallback
    return 'English';
  };

  const handleMobileNavClick = (href) => {
    setIsOpen(false);
    setMobileAboutOpen(false);
  };

  // Load events for ticker
  useEffect(() => {
    const loadEvents = async () => {
      try {
        const events = await fetchAllEventsPages();
        const now = new Date();
        
        const upcomingAndLive = Array.isArray(events) 
          ? events.filter(event => {
              if (event.isPublished === false) return false;
              if (event.status === 'Completed' || event.status === 'Cancelled') return false;
              if (event.status === 'Upcoming' || event.status === 'Ongoing') return true;
              
              try {
                const { start, end } = getEventStartEnd(event);
                if (!start || !end) return false;
                return start >= now || (now >= start && now <= end);
              } catch (e) {
                return false;
              }
            })
          : [];
        
        const sorted = upcomingAndLive.sort((a, b) => {
          try {
            const { start: startA } = getEventStartEnd(a);
            const { start: startB } = getEventStartEnd(b);
            const tA = startA ? startA.getTime() : 0;
            const tB = startB ? startB.getTime() : 0;
            return tA - tB;
          } catch (e) {
            return 0;
          }
        });
        
        setUpcomingEvents(sorted.slice(0, 4).map(e => ({
          id: e._id || e.id,
          title: e.title,
          date: (e.date || e.startDate || '').toString().slice(0, 10)
        })));
      } catch (e) {
        setUpcomingEvents([]);
      }
    };
    
    loadEvents();
    
    const handleDataChange = (event) => {
      if (event.detail && event.detail.type === 'events') {
        loadEvents();
      }
    };
    
    window.addEventListener('dataChanged', handleDataChange);
    return () => window.removeEventListener('dataChanged', handleDataChange);
  }, []);

  return (
    <div className="header-wrapper">
      {/* Logo Section - Left */}
      <div className="header-logo-area flex-shrink-0">
        <Link to="/" className="header-logo-link">
          <img 
            src="/images/logo.jpeg" 
            alt="IFM Logo" 
            className="header-logo-img max-w-full h-auto"
            onError={(e) => {
              e.target.style.display = 'none';
              const fallback = e.target.nextElementSibling;
              if (fallback) fallback.style.display = 'inline-block';
            }}
          />
          <span className="header-logo-text" style={{ display: 'none' }}>IFM</span>
        </Link>
      </div>

      {/* Navbar and Event Ticker - Right */}
      <div className="header-content-area min-w-0 flex-1" style={{ overflow: 'visible' }}>
        {/* Navbar */}
        <nav className="navbar w-full" style={{ zIndex: 1000, overflow: 'visible', position: 'relative' }}>
          <div className="container w-full max-w-full" style={{ overflow: 'visible' }}>
            <div className="navbar-container w-full" style={{ overflow: 'visible' }}>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2 flex-1 min-w-0 overflow-x-auto" style={{ overflow: 'visible', position: 'relative' }}>
            {navigation.map((item) => (
              <div key={item.name} className="relative flex-shrink-0 whitespace-nowrap" style={{ zIndex: 1001, overflow: 'visible' }}>
                {item.hasDropdown ? (
                  <div className="relative" ref={dropdownRef} style={{ overflow: 'visible' }}>
                    <button
                      ref={aboutButtonRef}
                      onClick={() => setIsAboutOpen(!isAboutOpen)}
                      onMouseEnter={() => setIsAboutOpen(true)}
                      className={`nav-link flex items-center gap-1 ${item.current ? 'active' : ''}`}
                      aria-expanded={isAboutOpen}
                      aria-haspopup="true"
                    >
                      {item.name}
                      <ChevronDown className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${isAboutOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {isAboutOpen && (
                      <div 
                        className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-xl backdrop-blur-sm"
                        style={{ 
                          zIndex: 1001,
                          overflow: 'visible',
                          position: 'absolute',
                          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15), 0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}
                        onMouseEnter={() => setIsAboutOpen(true)}
                        onMouseLeave={() => setIsAboutOpen(false)}
                      >
                        <div className="py-1.5" style={{ overflow: 'visible' }}>
                          {item.dropdownItems.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.href}
                              to={dropdownItem.href}
                              className={`block px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-primary-50 hover:text-black transition-all duration-200 rounded-md mx-1.5 ${
                                location.pathname === dropdownItem.href ? 'bg-primary-50 text-primary-600 font-semibold' : ''
                              }`}
                              onClick={() => setIsAboutOpen(false)}
                            >
                              {dropdownItem.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`nav-link whitespace-nowrap ${item.current ? 'active' : ''}`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}

            {/* Important Links Dropdown */}
            <div className="relative flex-shrink-0 whitespace-nowrap" ref={importantLinksDropdownRef} style={{ zIndex: 1001, overflow: 'visible' }}>
              <button
                ref={importantLinksButtonRef}
                onClick={() => setIsImportantLinksOpen(!isImportantLinksOpen)}
                onMouseEnter={() => setIsImportantLinksOpen(true)}
                className="nav-link flex items-center gap-1.5"
                aria-expanded={isImportantLinksOpen}
                aria-haspopup="true"
              >
                <span className="whitespace-nowrap">{getText('nav.importantLinks') || 'Important Links'}</span>
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 flex-shrink-0 ${isImportantLinksOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isImportantLinksOpen && (
                <div 
                  className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-xl backdrop-blur-sm"
                  style={{ 
                    zIndex: 1001,
                    overflow: 'visible',
                    position: 'absolute',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15), 0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseEnter={() => setIsImportantLinksOpen(true)}
                  onMouseLeave={() => setIsImportantLinksOpen(false)}
                >
                  <div className="py-1.5" style={{ overflow: 'visible' }}>
                    {importantLinks.map((link) => (
                      <a
                        key={link.name}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-primary-50 hover:text-black transition-all duration-200 rounded-md mx-1.5 group"
                        onClick={() => setIsImportantLinksOpen(false)}
                      >
                        <FileText className="h-4 w-4 flex-shrink-0 transition-transform duration-200 group-hover:scale-110" />
                        <span className="flex-1 break-words">{link.name}</span>
                        <ExternalLink className="h-3 w-3 flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity duration-200" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Language Switcher - Between Important Links and Social Media - Hidden on Admin Routes */}
            {!isAdminRoute && (
              <div className="hidden lg:flex items-center relative flex-shrink-0" ref={langDropdownRef} style={{ zIndex: 1001, overflow: 'visible' }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const newState = !isLangOpen;
                    setIsLangOpen(newState);
                  }}
                  className="nav-link flex items-center gap-1.5"
                  aria-expanded={isLangOpen}
                  aria-haspopup="true"
                  style={{ cursor: 'pointer' }}
                >
                  <Globe className="h-4 w-4 flex-shrink-0" />
                  <span className="whitespace-nowrap">{getCurrentLanguageName() || 'English'}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 flex-shrink-0 ${isLangOpen ? 'rotate-180' : ''}`} />
                </button>

                {isLangOpen && languages && Object.keys(languages).length > 0 && (
                  <div 
                    className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-xl backdrop-blur-sm"
                    style={{ 
                      zIndex: 1001,
                      overflow: 'visible',
                      position: 'absolute',
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15), 0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseEnter={() => setIsLangOpen(true)}
                    onMouseLeave={() => setIsLangOpen(false)}
                  >
                    <div className="py-1.5" style={{ overflow: 'visible' }}>
                      {Object.entries(languages).map(([code, lang]) => (
                        <button
                          key={code}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLanguageChange(code);
                          }}
                          className={`w-full text-left px-4 py-2.5 text-sm font-medium rounded-md mx-1.5 transition-all duration-200 ${
                            currentLang === code ? 'bg-primary-600 text-white hover:bg-primary-700 hover:text-black font-semibold' : 'text-gray-700 hover:bg-primary-50 hover:text-black'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span>{lang?.name || code}</span>
                            {currentLang === code && (
                              <span className="text-white font-bold">✓</span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Social Media Icons - Direct Links */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {socialMediaLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200 group flex-shrink-0"
                    aria-label={social.name}
                    title={social.name}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0 transition-transform duration-200 group-hover:scale-110" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Tablet Navigation - Shows between md and lg */}
          <div className="hidden md:flex lg:hidden items-center gap-1.5 flex-1 min-w-0 overflow-x-auto">
            {navigation.slice(0, 4).map((item) => (
              <div key={item.name} className="relative flex-shrink-0" style={{ zIndex: 1001, overflow: 'visible' }}>
                {item.hasDropdown ? (
                  <div className="relative" ref={dropdownRef} style={{ overflow: 'visible' }}>
                    <button
                      ref={aboutButtonRef}
                      onClick={() => setIsAboutOpen(!isAboutOpen)}
                      onMouseEnter={() => setIsAboutOpen(true)}
                      className={`nav-link flex items-center gap-1 text-sm px-2 py-1.5 ${item.current ? 'active' : ''}`}
                      aria-expanded={isAboutOpen}
                      aria-haspopup="true"
                    >
                      {item.name}
                      <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${isAboutOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {isAboutOpen && (
                      <div 
                        className="absolute top-full left-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-xl backdrop-blur-sm"
                        style={{ zIndex: 1001 }}
                        onMouseEnter={() => setIsAboutOpen(true)}
                        onMouseLeave={() => setIsAboutOpen(false)}
                      >
                        <div className="py-1.5">
                          {item.dropdownItems.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.href}
                              to={dropdownItem.href}
                              className={`block px-3 py-2 text-xs font-medium text-gray-700 hover:bg-primary-50 hover:text-black transition-all duration-200 rounded-md mx-1 ${
                                location.pathname === dropdownItem.href ? 'bg-primary-50 text-primary-600 font-semibold' : ''
                              }`}
                              onClick={() => setIsAboutOpen(false)}
                            >
                              {dropdownItem.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`nav-link text-sm px-2 py-1.5 ${item.current ? 'active' : ''}`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
            
            {/* Tablet Language Switcher - Hidden on Admin Routes */}
            {!isAdminRoute && (
              <div className="relative flex-shrink-0" ref={langDropdownRef} style={{ zIndex: 1001, overflow: 'visible' }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const newState = !isLangOpen;
                    setIsLangOpen(newState);
                  }}
                  className="nav-link flex items-center gap-1 text-sm px-2 py-1.5"
                  aria-expanded={isLangOpen}
                  aria-haspopup="true"
                  style={{ cursor: 'pointer' }}
                >
                  <Globe className="h-3 w-3 flex-shrink-0" />
                  <span className="whitespace-nowrap text-xs">{getCurrentLanguageName() || 'English'}</span>
                </button>

                {isLangOpen && languages && Object.keys(languages).length > 0 && (
                  <div 
                    className="absolute right-0 top-full mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-xl backdrop-blur-sm"
                    style={{ zIndex: 1001 }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseEnter={() => setIsLangOpen(true)}
                    onMouseLeave={() => setIsLangOpen(false)}
                  >
                    <div className="py-1">
                      {Object.entries(languages).map(([code, lang]) => (
                        <button
                          key={code}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLanguageChange(code);
                          }}
                          className={`w-full text-left px-3 py-2 text-xs font-medium rounded-md mx-1 transition-all duration-200 ${
                            currentLang === code ? 'bg-primary-600 text-white hover:bg-primary-700 font-semibold' : 'text-gray-700 hover:bg-primary-50 hover:text-black'
                          }`}
                        >
                          {lang?.name || code}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tablet Social Media - Smaller */}
            <div className="flex items-center gap-1 flex-shrink-0">
              {socialMediaLinks.slice(0, 2).map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                    aria-label={social.name}
                    title={social.name}
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center flex-shrink-0">
            <button
              onClick={() => {
                setIsOpen(!isOpen);
                if (!isOpen) {
                  setMobileAboutOpen(false);
                  setMobileImportantLinksOpen(false);
                }
              }}
              className="inline-flex items-center justify-center p-2.5 rounded-md text-gray-700 hover:text-primary hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors touch-manipulation"
              aria-label="Toggle menu"
              aria-expanded={isOpen}
            >
              {isOpen ? (
                <X className="block h-6 w-6 flex-shrink-0" />
              ) : (
                <Menu className="block h-6 w-6 flex-shrink-0" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="lg:hidden border-t border-gray-200 bg-white max-h-[calc(100vh-200px)] overflow-y-auto overscroll-contain">
          <div className="px-3 pt-3 pb-4 space-y-1">
            {navigation.map((item) => (
              <div key={item.name}>
                {item.hasDropdown ? (
                  <div>
                    <button
                      onClick={() => setMobileAboutOpen(!mobileAboutOpen)}
                      className={`w-full flex items-center justify-between px-3 py-3 text-base font-medium rounded-md transition-colors touch-manipulation ${
                        item.current ? 'bg-primary-50 text-primary-600' : 'text-gray-700 hover:bg-gray-100 active:bg-gray-200'
                      }`}
                    >
                      <span>{item.name}</span>
                      <ChevronDown className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${mobileAboutOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {mobileAboutOpen && (
                      <div className="pl-4 mt-1 space-y-0.5">
                        {item.dropdownItems.map((dropdownItem) => (
                          <Link
                            key={dropdownItem.href}
                            to={dropdownItem.href}
                            onClick={() => handleMobileNavClick(dropdownItem.href)}
                            className={`block px-3 py-2.5 text-sm rounded-md transition-colors touch-manipulation ${
                              location.pathname === dropdownItem.href
                                ? 'bg-primary-100 text-primary-700 font-semibold'
                                : 'text-gray-600 hover:bg-gray-100 active:bg-gray-200'
                            }`}
                          >
                            {dropdownItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    onClick={() => handleMobileNavClick(item.href)}
                    className={`block px-3 py-3 text-base font-medium rounded-md transition-colors touch-manipulation ${
                      item.current ? 'bg-primary-50 text-primary-600' : 'text-gray-700 hover:bg-gray-100 active:bg-gray-200'
                    }`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
            
            {/* Mobile Important Links */}
            <div>
              <button
                onClick={() => setMobileImportantLinksOpen(!mobileImportantLinksOpen)}
                className="w-full flex items-center justify-between px-3 py-3 text-base font-medium rounded-md transition-colors text-gray-700 hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
              >
                <span>{getText('nav.importantLinks') || 'Important Links'}</span>
                <ChevronDown className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${mobileImportantLinksOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileImportantLinksOpen && (
                <div className="pl-4 mt-1 space-y-0.5">
                  {importantLinks.map((link) => (
                    <a
                      key={link.name}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => handleMobileNavClick(link.url)}
                      className="flex items-center gap-2 px-3 py-2.5 text-sm rounded-md transition-colors text-gray-600 hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
                    >
                      <FileText className="h-4 w-4 flex-shrink-0" />
                      <span>{link.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Social Media Icons */}
            <div className="px-3 py-3 border-t border-gray-200">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm font-medium text-gray-700 flex-shrink-0">{getText('nav.mobile.followUs')}</span>
                <div className="flex items-center gap-2 flex-wrap">
                  {socialMediaLinks.map((social) => {
                    const Icon = social.icon;
                    return (
                      <a
                        key={social.name}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleMobileNavClick(social.url)}
                        className="p-2.5 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200 touch-manipulation active:bg-primary-100"
                        aria-label={social.name}
                      >
                        <Icon className="h-5 w-5 flex-shrink-0" />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Mobile Language Switcher - Hidden on Admin Routes */}
            {!isAdminRoute && (
              <div className="px-3 py-3 border-t border-gray-200">
                <div className="text-sm font-semibold text-gray-700 mb-2">{getText('nav.mobile.language')}</div>
                <div className="space-y-1.5">
                  {Object.entries(languages).map(([code, lang]) => (
                    <button
                      key={code}
                      onClick={() => {
                        handleLanguageChange(code);
                        setIsOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2.5 text-sm rounded-md transition-colors touch-manipulation ${
                        currentLang === code 
                          ? 'bg-primary-600 text-white active:bg-primary-700' 
                          : 'text-gray-700 hover:bg-gray-100 active:bg-gray-200'
                      }`}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
        </nav>

        {/* Event Ticker */}
        <div className="header-event-ticker w-full overflow-hidden">
          <div className="container w-full max-w-full">
            <div className="flex items-center gap-2 md:gap-3 min-w-0">
              <div className="flex items-center gap-1.5 md:gap-2 font-semibold text-xs md:text-sm flex-shrink-0" style={{ color: '#374151' }}>
                <Calendar size={14} className="md:w-4 md:h-4 flex-shrink-0" style={{ color: '#374151' }}/> 
                <span className="whitespace-nowrap hidden sm:inline">{getText('home.upcomingEvents')}</span>
                <span className="whitespace-nowrap sm:hidden">Events</span>
              </div>
              <div className="news-ticker-track flex-1 min-w-0 overflow-hidden">
                <div className="news-ticker-inner">
                  {upcomingEvents.length > 0 ? (
                    upcomingEvents.map((ev, idx) => (
                      <div key={ev.id + '-' + idx} className="news-ticker-item">
                        <span className="badge">NEW</span>
                        <span className="date-pill">{ev.date}</span>
                        <Link to={`/events/${ev.id}`} className="truncate" style={{ color: 'inherit', textDecoration: 'none', fontWeight: 600 }}>
                          {ev.title}
                        </Link>
                        <span className="chev">›</span>
                      </div>
                    ))
                  ) : (
                    <div className="news-ticker-item">
                      <span style={{ color: '#6b7280', fontStyle: 'italic' }}>No upcoming events</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navigation;
